import { useEffect, useMemo } from 'react';

import { useQuery } from '@apollo/client/react';
import { addDays, compareAsc, endOfDay, parseISO, startOfDay } from 'date-fns';

import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { subscribeFeedRefresh } from '@modules/_shared/utils/feed-refresh';
import {
  DashboardTasksCardItem,
  type Task,
} from '@modules/dashboard/dashboard-task/DashboardTasksCardItem';
import { DASHBOARD_TASKS_ANCHOR } from '@modules/dashboard/dashboardAnchors';
import { GET_DASHBOARD_TASKS } from '@modules/dashboard/dashboardQueries';
import { useDashboardClientList } from '@modules/dashboard/hooks/useDashboardClientList';
import { useScrollToAnchor } from '@modules/dashboard/hooks/useScrollToAnchor';
import { DashboardCard } from '@modules/dashboard/shared/DashboardCard';
import { useLocale } from '@shared/hooks/locale';

const EMPTY_TASKS: Task[] = [];
const CALL_OR_TASK_TYPES = new Set(['call', 'task']);

const VIEW_CALENDAR_LINK = `/calendar?${new URLSearchParams([
  ['activityStatus_in', 'done'],
  ['activityStatus_in', 'pending'],
  ['activityType_in', 'visit'],
  ['activityType_in', 'call'],
  ['activityType_in', 'task'],
]).toString()}`;

type TaskMeta = {
  task: Task;
  consolidatedDueLocal: Date;
  startDateLocal: Date | null;
  isTimed: boolean;
  isToday: boolean;
  isOverdue: boolean;
  index: number;
};

export const DashboardTasksCard = () => {
  const { t } = useLocale();
  const { me } = useAppData();
  const { data, refetch, loading, error } = useQuery(GET_DASHBOARD_TASKS, {
    variables: {
      userId: me?.id ?? '',
    },
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    const unsubscribe = subscribeFeedRefresh(() => {
      refetch();
    });

    return unsubscribe;
  }, [refetch]);

  const tasks = data?.allTasks ?? EMPTY_TASKS;

  const orderedMetas = useMemo(() => {
    const nowLocal = new Date();
    const startOfTodayLocal = startOfDay(nowLocal);
    const startOfTomorrowLocal = addDays(startOfTodayLocal, 1);

    const metas: TaskMeta[] = tasks
      .map((task, index) => {
        const startDateLocal = task.start_date
          ? parseISO(`${task.start_date}T00:00:00`)
          : null;
        const dueAtLocal = task.due_at ? parseISO(task.due_at) : null;
        const isCallOrTask = CALL_OR_TASK_TYPES.has(task.activity_type);
        const dueDateIso = task.due_at ? task.due_at.slice(0, 10) : null;
        const useStartDateFallback =
          isCallOrTask &&
          startDateLocal != null &&
          (dueAtLocal == null || dueDateIso !== task.start_date);

        let consolidatedDueLocal: Date | null = null;

        if (isCallOrTask) {
          if (useStartDateFallback && startDateLocal != null) {
            consolidatedDueLocal = endOfDay(startDateLocal);
          } else if (dueAtLocal) {
            consolidatedDueLocal = dueAtLocal;
          } else if (startDateLocal) {
            consolidatedDueLocal = endOfDay(startDateLocal);
          }
        } else {
          if (dueAtLocal) {
            consolidatedDueLocal = dueAtLocal;
          } else if (startDateLocal) {
            consolidatedDueLocal = endOfDay(startDateLocal);
          }
        }

        if (consolidatedDueLocal == null) {
          return null;
        }

        const isTimed = Boolean(dueAtLocal) && !useStartDateFallback;
        const isToday =
          consolidatedDueLocal >= startOfTodayLocal &&
          consolidatedDueLocal < startOfTomorrowLocal;
        const isOverdue = consolidatedDueLocal < startOfTodayLocal;

        if (!isToday && !isOverdue) {
          return null;
        }

        return {
          task,
          consolidatedDueLocal,
          startDateLocal,
          isTimed,
          isToday,
          isOverdue,
          index,
        } satisfies TaskMeta;
      })
      .filter((meta): meta is TaskMeta => meta != null);

    const todayTimed: TaskMeta[] = [];
    const todayUntimed: TaskMeta[] = [];
    const overdueTasks: TaskMeta[] = [];

    metas.forEach(meta => {
      if (meta.isToday) {
        if (meta.isTimed) {
          todayTimed.push(meta);
        } else {
          todayUntimed.push(meta);
        }
      } else if (meta.isOverdue) {
        overdueTasks.push(meta);
      }
    });

    const sortByConsolidatedDueAsc = (a: TaskMeta, b: TaskMeta) => {
      if (a.consolidatedDueLocal && b.consolidatedDueLocal) {
        return compareAsc(a.consolidatedDueLocal, b.consolidatedDueLocal);
      }
      if (a.consolidatedDueLocal) {
        return -1;
      }
      if (b.consolidatedDueLocal) {
        return 1;
      }

      return a.index - b.index;
    };

    const sortByConsolidatedDueDesc = (a: TaskMeta, b: TaskMeta) =>
      sortByConsolidatedDueAsc(b, a);

    const sortByStartDateAsc = (a: TaskMeta, b: TaskMeta) => {
      if (a.startDateLocal && b.startDateLocal) {
        return compareAsc(a.startDateLocal, b.startDateLocal);
      }
      if (a.startDateLocal) {
        return -1;
      }
      if (b.startDateLocal) {
        return 1;
      }
      return sortByConsolidatedDueAsc(a, b);
    };

    const sortOverdueTasks = (a: TaskMeta, b: TaskMeta) => {
      const dueCompare = sortByConsolidatedDueDesc(a, b);
      if (dueCompare !== 0) {
        return dueCompare;
      }
      if (a.isTimed && !b.isTimed) {
        return -1;
      }
      if (!a.isTimed && b.isTimed) {
        return 1;
      }
      return a.index - b.index;
    };

    todayTimed.sort(sortByConsolidatedDueAsc);
    todayUntimed.sort(sortByStartDateAsc);
    overdueTasks.sort(sortOverdueTasks);

    return [...todayTimed, ...todayUntimed, ...overdueTasks];
  }, [tasks]);

  const queryResult = useDashboardClientList({
    items: orderedMetas,
    loading,
    error,
  });
  const { highlighted } = useScrollToAnchor(DASHBOARD_TASKS_ANCHOR, !loading);

  return (
    <DashboardCard
      anchorId={DASHBOARD_TASKS_ANCHOR}
      highlighted={highlighted}
      title={t('My tasks')}
      actionLabel={t('View calendar')}
      actionLink={VIEW_CALENDAR_LINK}
      queryResult={queryResult}
      renderItem={(meta, key) => (
        <DashboardTasksCardItem
          key={key}
          task={meta.task}
          isOverdue={meta.isOverdue}
          showDueTime={meta.isTimed}
        />
      )}
      emptyStateMessage={t('You do not have any pending tasks')}
      emptyStateVariant="success"
    />
  );
};
